import { useToast } from 'braid-design-system';

import {
  createCriticalToast,
  createSuccessToast,
} from 'src/App/Components/ToastMessage/ToastMessages';
import type { UserDetails } from 'src/graphql/generated';
import { trackAlertShown } from 'src/utils/trackingUserActions';

import { useRemoveUserFromAccount } from './use-remove-user-from-account';
import { useSendPasswordResetEmail } from './use-send-password-reset-email';
import { useSendRegistrationEmail } from './use-send-registration-email';

export const useUserActions = () => {
  const showToast = useToast();
  const {
    mutate: sendPasswordResetEmail,
    loading: sendPasswordResetEmailLoading,
  } = useSendPasswordResetEmail();
  const {
    mutate: sendRegistrationEmail,
    loading: sendRegistrationEmailLoading,
  } = useSendRegistrationEmail();
  const { mutate: removeUserFromAccount } = useRemoveUserFromAccount();

  const handleRemoveUser = (userId: number) => {
    removeUserFromAccount({
      variables: { input: { userId } },
      onCompleted: () => {
        const successToast = createSuccessToast('Team member removed');
        showToast(successToast);
      },
      onError: () => {
        const criticalToast = createCriticalToast(
          'Remove request failed, please try again',
        );
        showToast(criticalToast);
      },
    });
  };

  const handleSendPasswordResetEmail = (user: UserDetails) => {
    sendPasswordResetEmail({
      variables: { input: { email: user.email } },
      onCompleted: () => {
        const successToast = createSuccessToast('Password reset email sent');
        showToast(successToast);
        trackAlertShown(successToast.message, user.id);
      },
      onError: () => {
        const criticalToast = createCriticalToast(
          'Password reset request unsuccessful, please try again.',
        );
        showToast(criticalToast);
        trackAlertShown(criticalToast.message, user.id);
      },
    });
  };

  const handleResendActivationEmail = (user: UserDetails) => {
    sendRegistrationEmail({
      variables: {
        input: {
          isInCSView: false,
          userId: user.id,
        },
      },
      onCompleted: () => {
        const successToast = createSuccessToast('Activation email sent');
        showToast(successToast);
        trackAlertShown(successToast.message, user.id);
      },
      onError: () => {
        const criticalToast = createCriticalToast(
          'Request to send activation email unsuccessful, please try again',
        );
        showToast(criticalToast);
        trackAlertShown(criticalToast.message, user.id);
      },
    });
  };

  return {
    handleSendPasswordResetEmail,
    handleResendActivationEmail,
    handleRemoveUser,
    sendPasswordResetEmailLoading,
    sendRegistrationEmailLoading,
  };
};
